import {
  BusinessCenter,
  Dashboard,
  FileDownload,
  Help,
  Layers,
  LocalOffer,
  MiscellaneousServices,
  QueryStats as MonitoringIcon,
  Person,
  Report,
  Settings,
  ShoppingCart,
  Widgets,
} from '@mui/icons-material';

import localization from '../../localization';
import {
  NEXWAY_SERVICES_RESTRICTIONS,
  PERMISSION_LEVEL,
  PERMISSION_RESOURCE,
} from '../../services/constants/permissions';
import { sideBarTitle } from '../../services/helpers/sectionHeaders';
import parentPaths from '../../services/paths';

const navConfig = [
  {
    items: [
      {
        id: 'dashboard',
        title: localization.t('sideBar.titles.dashboard'),
        href: parentPaths.default,
        exact: true,
        isMain: true,
        icon: Dashboard,
        restrictions: NEXWAY_SERVICES_RESTRICTIONS,
      },
    ],
  },
  {
    subheader: localization.t('sideBar.subheaders.sales'),
    subheaderIcon: ShoppingCart,
    items: [
      {
        id: 'orders',
        title: sideBarTitle.orderlist,
        href: parentPaths.orderlist,
        restrictions: {
          resource: PERMISSION_RESOURCE.ORDER_SERVICE,
          level: PERMISSION_LEVEL.READ,
        },
      },
      {
        id: 'ordersExport',
        title: sideBarTitle.ordersExport,
        href: parentPaths.ordersExport.main,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_IAP_IMPORT_EXPORT,
          level: PERMISSION_LEVEL.EXPORT_ORDER,
        },
      },
      {
        id: 'orderCancellations',
        title: sideBarTitle.orderCancellations,
        href: parentPaths.orderCancellations,
        restrictions: {
          resource: PERMISSION_RESOURCE.ORDER_SERVICE,
          level: PERMISSION_LEVEL.CANCEL_ORDER,
        },
      },
      {
        id: 'subscriptions',
        title: sideBarTitle.subscriptions,
        href: parentPaths.subscriptions,
        restrictions: [
          [
            {
              resource: PERMISSION_RESOURCE.NX_ECOMMERCE_SUBSCRIPTION,
              level: PERMISSION_LEVEL.READ_SUBSCRIPTION,
            },
          ],
          // or
          [
            {
              resource: PERMISSION_RESOURCE.NX_IAP_SUBSCRIPTION,
              level: PERMISSION_LEVEL.READ_SUBSCRIPTIONS,
            },
          ],
        ],
      },
      {
        id: 'invoices-credit-notes',
        title: sideBarTitle.invoices,
        href: parentPaths.invoices,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_INVOICE_BUILDER,
          level: PERMISSION_LEVEL.GET,
        },
      },
      {
        id: 'invoicesExport',
        title: sideBarTitle.invoicesExport,
        href: parentPaths.invoicesExport,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_IAP_MARKETPLACE_ORDER,
          level: PERMISSION_LEVEL.READ,
        },
      },
      {
        id: 'licenses',
        title: sideBarTitle.licenses,
        href: parentPaths.licenses,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_LICENSE_MANAGER,
          level: PERMISSION_LEVEL.READ_LICENSES,
        },
      },
      {
        id: 'carts',
        title: sideBarTitle.carts,
        href: parentPaths.carts,
        restrictions: {
          resource: PERMISSION_RESOURCE.CART_SERVICE,
          level: PERMISSION_LEVEL.READ,
        },
      },
      {
        id: 'marketplaceOrders',
        title: sideBarTitle.marketplaceOrders,
        href: parentPaths.marketplaceOrders,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_IAP_MARKETPLACE_ORDER,
          level: PERMISSION_LEVEL.READ,
        },
      },
    ],
  },
  {
    subheader: localization.t('sideBar.subheaders.storeSetup'),
    subheaderIcon: Layers,
    items: [
      {
        id: 'stores',
        title: sideBarTitle.stores,
        href: parentPaths.stores,
        restrictions: {
          resource: PERMISSION_RESOURCE.STORE_SERVICE,
          level: PERMISSION_LEVEL.READ,
        },
      },
      {
        id: 'checkout-experience',
        title: sideBarTitle.checkoutpagebuilder,
        href: parentPaths.checkoutpagebuilder.main,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_IAP_DESIGN,
          level: PERMISSION_LEVEL.LIST_DESIGNS,
        },
      },
      {
        id: 'localization',
        title: sideBarTitle.localization,
        href: parentPaths.localization.main,
        restrictions: [
          [
            {
              resource: PERMISSION_RESOURCE.NX_IAP_DESIGN,
              level: PERMISSION_LEVEL.LIST_LEGAL_I18NS,
            },
          ],
          // or
          [
            {
              resource: PERMISSION_RESOURCE.NX_IAP_DESIGN,
              level: PERMISSION_LEVEL.LIST_DESIGNS,
            },
          ],
        ],
      },
      {
        id: 'email-builder',
        title: sideBarTitle.emailbuilder,
        href: parentPaths.emailbuilder,
        restrictions: {
          resource: PERMISSION_RESOURCE.EMAIL_BUILDER_SERVICE,
          level: PERMISSION_LEVEL.READ,
        },
      },
    ],
  },
  {
    subheader: localization.t('sideBar.subheaders.products'),
    subheaderIcon: Widgets,
    items: [
      {
        id: 'products',
        title: sideBarTitle.productlist,
        href: parentPaths.productlist,
        restrictions: {
          resource: PERMISSION_RESOURCE.PRODUCT_SERVICE,
          level: PERMISSION_LEVEL.READ,
        },
      },
      {
        id: 'price-models',
        title: sideBarTitle.pricemodels,
        href: parentPaths.pricemodels.main,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_IAP_PRICE,
          level: PERMISSION_LEVEL.READ,
        },
      },
      {
        id: 'import-export-prices',
        title: sideBarTitle.priceImportExport,
        href: parentPaths.importExportPrices,
        restrictions: [
          [
            {
              resource: PERMISSION_RESOURCE.NX_IAP_IMPORT_EXPORT,
              level: PERMISSION_LEVEL.EXPORT_FULL_PRICE,
            },
          ],
          // or
          [
            {
              resource: PERMISSION_RESOURCE.NX_IAP_IMPORT_EXPORT,
              level: PERMISSION_LEVEL.EXPORT_PRICE,
            },
          ],
          // or
          [
            {
              resource: PERMISSION_RESOURCE.NX_IAP_IMPORT_EXPORT,
              level: PERMISSION_LEVEL.IMPORT_PRICE,
            },
          ],
        ],
      },
      {
        id: 'cros-sell-up-sell-reco',
        title: sideBarTitle.recommendations,
        href: parentPaths.recommendations,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_PRODUCT_RECO,
          level: PERMISSION_LEVEL.READ,
        },
      },
      {
        id: 'fulfillment-packages',
        title: sideBarTitle.fulfillment,
        href: parentPaths.fulfillment.main,
        restrictions: [
          [
            {
              resource: PERMISSION_RESOURCE.NX_FULFILLMENT,
              level: PERMISSION_LEVEL.READ_PARTNERS,
            },
          ],
          [
            {
              resource: PERMISSION_RESOURCE.NX_LICENSE_KEY_PROVIDER,
              level: PERMISSION_LEVEL.LIST_PACKAGE,
            },
          ],
          [
            {
              resource: PERMISSION_RESOURCE.NX_LICENSE_MANAGER,
              level: PERMISSION_LEVEL.READ_LICENSE_PROVIDER_DEFINITIONS,
            },
          ],
        ],
      },
      {
        id: 'catalogs',
        title: sideBarTitle.catalogs,
        href: parentPaths.catalogs,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_CATALOG_FREE,
          level: PERMISSION_LEVEL.READ,
        },
      },
      {
        id: 'customFieldsDefinition',
        title: sideBarTitle.customFieldsDefinition,
        href: parentPaths.customFieldsDefinition,
        restrictions: {
          resource: PERMISSION_RESOURCE.PRODUCT_SERVICE,
          level: PERMISSION_LEVEL.READ,
        },
      },
    ],
  },
  {
    subheader: localization.t('sideBar.subheaders.marketing'),
    subheaderIcon: LocalOffer,
    items: [
      {
        id: 'discount-rules',
        title: sideBarTitle.discountrules,
        href: parentPaths.discountrules,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_IAP_DISCOUNT,
          level: PERMISSION_LEVEL.READ,
        },
      },
      {
        id: 'marketing-campaigns',
        title: sideBarTitle.campaigns,
        href: parentPaths.campaigns.campaigns,
        restrictions: [
          [
            {
              resource: PERMISSION_RESOURCE.NX_IAP_MARKETING_CAMPAIGN,
              level: PERMISSION_LEVEL.READ,
            },
          ],
          [
            {
              resource: PERMISSION_RESOURCE.NX_CAMPAIGN_CONFIGURATOR,
              level: PERMISSION_LEVEL.READ,
            },
          ],
        ],
      },
    ],
  },
  {
    subheader: localization.t('sideBar.subheaders.endUsers'),
    subheaderIcon: BusinessCenter,
    items: [
      {
        id: 'end-users',
        title: sideBarTitle.enduserlist,
        href: parentPaths.endusers,
        restrictions: [
          {
            resource: PERMISSION_RESOURCE.ENDUSER_SERVICE,
            level: PERMISSION_LEVEL.READ,
          },
        ],
      },
      {
        id: 'end-user-groups',
        title: sideBarTitle.endusergroups,
        href: parentPaths.endusergroups,
        restrictions: [
          {
            resource: PERMISSION_RESOURCE.ENDUSER_SERVICE,
            level: PERMISSION_LEVEL.READ,
          },
        ],
      },
      {
        id: 'resellers',
        title: sideBarTitle.resellers,
        href: parentPaths.resellers,
        restrictions: [
          {
            resource: PERMISSION_RESOURCE.ENDUSER_SERVICE,
            level: PERMISSION_LEVEL.READ,
          },
        ],
      },
    ],
  },
  {
    items: [
      {
        id: 'exportManagement',
        title: sideBarTitle.exportManagement,
        href: parentPaths.exportManagement.main,
        isMain: true,
        icon: FileDownload,
        restrictions: [
          [
            {
              resource: PERMISSION_RESOURCE.NX_IAP_IMPORT_EXPORT,
              level: PERMISSION_LEVEL.EXPORT_SUBSCRIPTION,
            },
          ],
          // or
          [
            {
              resource: PERMISSION_RESOURCE.NX_IAP_IMPORT_EXPORT,
              level: PERMISSION_LEVEL.EXPORT_PRODUCT,
            },
          ],
          // or
          [
            {
              resource: PERMISSION_RESOURCE.NX_IAP_IMPORT_EXPORT,
              level: PERMISSION_LEVEL.EXPORT_DISCOUNT,
            },
          ],
        ],
      },
    ],
  },
  {
    items: [
      {
        id: 'reports',
        title: sideBarTitle.reports,
        href: parentPaths.reports,
        isMain: true,
        icon: Report,
      },
    ],
  },
  {
    subheader: localization.t('sideBar.subheaders.settings'),
    subheaderIcon: Settings,
    items: [
      {
        id: 'users',
        title: sideBarTitle.users,
        href: parentPaths.users,
        restrictions: {
          resource: PERMISSION_RESOURCE.KEYCLOAK_SERVICE,
          level: PERMISSION_LEVEL.READ_IDENTITIES,
        },
      },
      {
        id: 'user-roles-profiles',
        title: sideBarTitle.userroles,
        href: parentPaths.userroles.main,
        restrictions: {
          resource: PERMISSION_RESOURCE.KEYCLOAK_SERVICE,
          level: PERMISSION_LEVEL.READ_ROLES,
        },
      },
      {
        id: 'crud-helper',
        title: sideBarTitle.crudHelper,
        href: parentPaths.crudHelper.main,
        exact: true,
        restrictions: {
          resource: PERMISSION_RESOURCE.NEXWAY_CENTER_FEATURE,
          level: PERMISSION_LEVEL.POWER_TOOLS,
        },
      },
      {
        id: 'crud-console',
        title: sideBarTitle.crudConsole,
        href: parentPaths.crudHelper.request,
        exact: true,
        restrictions: {
          resource: PERMISSION_RESOURCE.NEXWAY_CENTER_FEATURE,
          level: PERMISSION_LEVEL.POWER_TOOLS,
        },
      },

      // {
      //   id: 'service-configuration',
      //   title: sideBarTitle.serviceconfiguration,
      //   href: parentPaths.serviceconfiguration,
      // },
    ],
  },
  {
    subheader: localization.t('sideBar.subheaders.customers'),
    subheaderIcon: Person,
    items: [
      {
        id: 'customer',
        title: sideBarTitle.customerslist,
        href: parentPaths.customers,
        restrictions: {
          resource: PERMISSION_RESOURCE.CUSTOMER_SERVICE,
          level: PERMISSION_LEVEL.READ,
        },
      },
      {
        id: 'onboarding',
        title: sideBarTitle.onboarding,
        href: parentPaths.onboarding,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_IAP_ONBOARDING,
          level: PERMISSION_LEVEL.READ,
        },
      },
      {
        id: 'remittables',
        title: sideBarTitle.remittables,
        href: parentPaths.remittables,
      },
      {
        id: 'audits',
        title: sideBarTitle.audits,
        href: parentPaths.audits,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_IAP_AUDIT,
          level: PERMISSION_LEVEL.READ_AUDIT_ITEMS,
        },
      },
      {
        id: 'event-notifications',
        title: sideBarTitle.notifications,
        href: parentPaths.notifications.main,
        restrictions: [
          [
            {
              resource: PERMISSION_RESOURCE.NX_CUSTOMER_NOTIFIER,
              level: PERMISSION_LEVEL.READ_NOTIFICATIONS,
            },
          ],
          [
            {
              resource: PERMISSION_RESOURCE.NX_CUSTOMER_NOTIFIER,
              level: PERMISSION_LEVEL.READ_RECEIVERS,
            },
          ],
          [
            {
              resource: PERMISSION_RESOURCE.NX_CUSTOMER_NOTIFIER,
              level: PERMISSION_LEVEL.READ_NOTIFICATIONS_DEFINITIONS,
            },
          ],
        ],
      },
    ],
  },
  {
    subheader: localization.t('sideBar.subheaders.monitoring'),
    subheaderIcon: MonitoringIcon,
    items: [
      {
        id: 'referential-manager',
        title: sideBarTitle.subscriptionMonitoring,
        href: parentPaths.subscriptionMonitoring,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_SUB_MONITORING,
          level: PERMISSION_LEVEL.OPERATE,
        },
      },
    ],
  },
  {
    subheader: localization.t('sideBar.subheaders.serviceConfiguration'),
    subheaderIcon: MiscellaneousServices,
    items: [
      {
        id: 'referential-manager',
        title: sideBarTitle.referentialManager,
        href: parentPaths.referentialManager.main,
        restrictions: {
          resource: PERMISSION_RESOURCE.NX_REFERENTIAL,
          level: PERMISSION_LEVEL.READ,
        },
      },
      {
        id: 'subsidiary-manager',
        title: sideBarTitle.subsidiaryManager,
        href: parentPaths.subsidiaryManager.main,
        restrictions: [
          {
            resource: PERMISSION_RESOURCE.NX_SUBSIDIARY,
            level: PERMISSION_LEVEL.READ,
          },
        ],
      },
      {
        id: 'tncManager',
        title: sideBarTitle.tncManager,
        href: parentPaths.tncManager.main,
        restrictions: [
          {
            resource: PERMISSION_RESOURCE.NX_TANDC,
            level: PERMISSION_LEVEL.READ,
          },
        ],
      },
    ],
  },
  {
    subheader: localization.t('sideBar.subheaders.help'),
    subheaderIcon: Help,
    items: [
      {
        id: 'api',
        title: localization.t('sideBar.titles.apiDocumentation'),
        href: parentPaths.apidocumentation,
        external: true,
      },
      {
        id: 'product-documentation',
        title: sideBarTitle['product-documentation'],
        href: parentPaths.productdocumentation,
        external: true,
      },
    ],
  },
];

export default navConfig;
